import { CurrencyPipe, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Product } from '../../../shared/interfaces/product';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { PaymentSuffixPipe } from '../../../shared/pipes/payment-suffix.pipe';
import { CalculatePaymentWithSalePipe } from '../../pipes/calculate-payment-with-sale.pipe';
import { ProductsService } from '../../../admin/services/products.service';
import { Sale } from '../../../shared/interfaces/sale';
import { ProductFamiliesUtil } from '../step-display-products/utils/product-families-util';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-product-card',
    standalone: true,
    imports: [
        NgIf,
        NgFor,
        CurrencyPipe,
        TruncatePipe,
        CalculatePaymentWithSalePipe,
        PaymentSuffixPipe,
    ],
    templateUrl: './product-card.component.html',
    styleUrls: ['./product-card.component.scss'],
})
export class ProductCardComponent {
    @Input() public descriptionLimit = 50;

    @Input({ required: true }) public product!: Product;

    @Input() public sale?: Sale;

    @Input() public selected = false;

    @Input() public selectedMap?: { [productId: number]: boolean | undefined };

    @Input() public showCart = true;

    @Input() public showToggleButton = true;

    @Output() public readonly toggle = new EventEmitter<Product>();

    @Input() public variants: Product[] = [];

    private _selectedVariant?: Product;

    public constructor(private readonly productsService: ProductsService) {
        //
    }

    public get chipLabels(): Map<number, string> {
        return ProductFamiliesUtil.chipLabelsForVariants(this.variants.length > 0 ? this.variants : [this.product]);
    }

    public get displayed(): Product {
        return this.lockedVariant ?? this._selectedVariant ?? this.product;
    }

    public get isSelected(): boolean {
        if (this.selectedMap) {
            return this.selectedMap[this.displayed.id] ?? false;
        }
        return this.selected;
    }

    public get lockedVariant(): Product | null {
        if (!this.selectedMap || this.variants.length === 0) {
            return null;
        }
        return this.variants.find((v) => this.selectedMap![v.id]) ?? null;
    }

    public get showColorPicker(): boolean {
        return this.variants.length > 1 && ProductFamiliesUtil.isColorPickerFamily(this.variants);
    }

    public isVariantDisabled(v: Product): boolean {
        return this.lockedVariant !== null && this.lockedVariant.id !== v.id;
    }

    public labelFor(variant: Product): string {
        return this.chipLabels.get(variant.id) ?? variant.part_number ?? '';
    }

    public onProductDetails(): void {
        this.productsService.openProductDetails(this.displayed, this.sale, {
            selected: this.isSelected,
            showToggleButton: this.showToggleButton,
            toggle: this.toggle,
        });
    }

    public onSelectVariant(event: Event, variant: Product): void {
        event.stopPropagation();
        if (this.isVariantDisabled(variant)) {
            return;
        }
        this._selectedVariant = variant;
    }

    public onToggle(event: Event): void {
        event.stopPropagation();
        this.toggle.emit(this.displayed);
    }
}
